import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../services/axios';

export const loginEmployee = createAsyncThunk(
  'employee/login',
  async (credentials, thunkAPI) => {
    try {
      const response = await axiosInstance.post('/employee/login', credentials);
      // Store token and user data on successful login
      localStorage.setItem('token', response.data.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.data.employee));
      return response.data;
    } catch (error) {
      // Check if it's a 402 status code which indicates password update required
      if (error.response?.status === 402) {
        // Return an object that indicates a password update is required
        // Also store any temporary token provided for the password update
        console.error('Password update required:', error.response.data);
        return thunkAPI.fulfillWithValue({
          requirePasswordUpdate: true,
          temporaryToken: error.response.data?.data.temporaryToken || null,
          message: error.response?.data?.message || 'Password update required'
        });
      }
      return thunkAPI.rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

export const createEmployee = createAsyncThunk(
    'employee/signup',
    async (credentials, thunkAPI) => {
        try {
          const response = await axiosInstance.post('/employee/signup', {
            ...credentials
          });
          return response.data;
        } catch (error) {
          return thunkAPI.rejectWithValue(error.response?.data?.message || 'employee signup failed');
        }
      }
  );
export const editEmployee = createAsyncThunk('employee/edit', async (credentials, thunkAPI) => {
    try {
        const response = await axiosInstance.put(`/employee/${credentials.id}`, {
            ...credentials
        });
        return response.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(error.response?.data?.message || 'employee update failed');
    }
});

export const getAllEmployees = createAsyncThunk(
    'employee/getAll',
    async (_, thunkAPI) => {
        try {
          const response = await axiosInstance.get('/employee');
          return response.data;
        } catch (error) {
          return thunkAPI.rejectWithValue(error.response?.data?.message || 'Failed to fetch employees');
        }
      }
  );
export const employeeStatusUpdate = createAsyncThunk(
    'employee/status',
    async (credentials, thunkAPI) => {
        try {
          const response = await axiosInstance.put(`/employee/status/${credentials.id}`, {
            ...credentials
          });
          return response.data;
        } catch (error) {
          return thunkAPI.rejectWithValue(error.response?.data?.message || 'Failed to update employee status');
        }
      }
  );    export const employeeDelete = createAsyncThunk(
    'employee/delete',
    async (credentials, thunkAPI) => {
        try {
          const response = await axiosInstance.delete(`/employee/${credentials.id}`);
          // Store the ID in the response for use in the reducer
          return { ...response.data, deleteId: credentials.id };
        } catch (error) {
          return thunkAPI.rejectWithValue(error.response?.data?.message || 'Failed to delete employee');
        }
      }
  );  export const employeeResetPassword = createAsyncThunk(
    'employee/resetPassword',
    async (credentials, thunkAPI) => {
        try {
          const response = await axiosInstance.post(`/employee/reset-password`, {
            ...credentials
          });
          return response.data;
        } catch (error) {
          return thunkAPI.rejectWithValue(error.response?.data?.message || 'Failed to reset employee password');        }
      }
  );
// Update Employee Password
export const updateEmployeePassword = createAsyncThunk(
  'employee/updatePassword',
  async (passwordData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put('/employee/update/password', passwordData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const employeeSlice = createSlice({
  name: "employee", 
  initialState: {
    employees: [], // List of employees
    employeeLoading: false,
    error: null,
    user: JSON.parse(localStorage.getItem('user')) || null,
    token: localStorage.getItem('token') || null,
    isAuthenticated: !!localStorage.getItem('token'),
    passwordUpdateRequired: false,
    temporaryToken: null
  },
  reducers: {
     logout: (state) => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      state.passwordUpdateRequired = false;
      state.temporaryToken = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      localStorage.setItem('user', JSON.stringify(action.payload));
    },
    resetPasswordUpdateRequired: (state) => {
      state.passwordUpdateRequired = false;
      state.temporaryToken = null;
    }
  },
  extraReducers: (builder) => {
    builder      // Create employee
      .addCase(createEmployee.pending, (state) => {
        state.employeeLoading = true;
        state.error = null;
      })
      .addCase(createEmployee.fulfilled, (state, action) => {
        state.employeeLoading = false;
        state.employees.push(action.payload.data);
      })
      .addCase(createEmployee.rejected, (state, action) => {
        state.employeeLoading = false;
        state.error = action.payload || action.error.message;
      })
        // Get all employees
      .addCase(getAllEmployees.pending, (state) => {
        state.employeeLoading = true;
        state.error = null;
      })      
      .addCase(getAllEmployees.fulfilled, (state, action) => {
        state.employeeLoading = false;
        // Detailed logging to debug the issue
        // Make sure we're setting an array or falling back to empty array
        state.employees = action.payload.data ? action.payload.data : [];
      })
      .addCase(getAllEmployees.rejected, (state, action) => {
        state.employeeLoading = false;
        state.error = action.payload || action.error.message;
      })      // Edit employee
      .addCase(editEmployee.pending, (state) => {
        state.employeeLoading = true;
        state.error = null;
      })
      .addCase(editEmployee.fulfilled, (state, action) => {
        state.employeeLoading = false;
        // Update the employees array with the edited employee
        const index = state.employees.findIndex(employee => employee.id === action.payload.data.id);
        if (index !== -1) {
          state.employees[index] = action.payload.data;
        }
      })
      .addCase(editEmployee.rejected, (state, action) => {
        state.employeeLoading = false;
        state.error = action.payload || action.error.message;
      })        // Employee status update
      .addCase(employeeStatusUpdate.pending, (state) => {
        state.employeeLoading = true;
        state.error = null;
      })
      .addCase(employeeStatusUpdate.fulfilled, (state, action) => {
        state.employeeLoading = false;
        // Update the employee array with the edited employee
        const index = state.employees.findIndex(employee => employee.id === action.payload.data.id);
        if (index !== -1) {
          state.employees[index] = action.payload.data;
        }
      })
      .addCase(employeeStatusUpdate.rejected, (state, action) => {
        state.employeeLoading = false;
        state.error = action.payload || action.error.message;      })//   Employee delete
    .addCase(employeeDelete.pending, (state) => {
      state.employeeLoading = true;
      state.error = null;
    })    
    .addCase(employeeDelete.fulfilled, (state, action) => {
      state.employeeLoading = false;
      console.log("employeeDelete.fulfilled - Full payload:", action.payload);
      
      // Use deleteId from the action that we added in the thunk
      const idToDelete = action.payload.deleteId;
      console.log("employeeDelete.fulfilled - ID to delete:", idToDelete);
      
      // Remove the deleted employee from the employees array
      state.employees = state.employees.filter(employee => employee.id !== idToDelete);
    })
    .addCase(employeeDelete.rejected, (state, action) => {
      state.employeeLoading = false;
      state.error = action.payload || action.error.message;
    })    // Employee reset password
    .addCase(employeeResetPassword.pending, (state) => {
      state.employeeLoading = true;
      state.error = null;
    })
    .addCase(employeeResetPassword.fulfilled, (state, action) => {
      state.employeeLoading = false;
      // Handle successful password reset if needed
    })    
    .addCase(employeeResetPassword.rejected, (state, action) => {
      state.employeeLoading = false;
      state.error = action.payload || action.error.message;
    })      // Login employee cases
    .addCase(loginEmployee.pending, (state) => {
      state.employeeLoading = true;
      state.error = null;
      state.passwordUpdateRequired = false;
      state.temporaryToken = null;
    })
    .addCase(loginEmployee.fulfilled, (state, action) => {
      state.employeeLoading = false;
      
      // Check if password update is required
      if (action.payload.requirePasswordUpdate) {
        // Handle password update requirement
        state.passwordUpdateRequired = true;
        state.temporaryToken = action.payload.temporaryToken;
        state.error = action.payload.message;
        state.isAuthenticated = false;
      } else {
        // Normal successful login
        state.user = action.payload.data.employee;
        state.token = action.payload.data.token;
        state.isAuthenticated = true;
        state.error = null;
        state.passwordUpdateRequired = false;
        state.temporaryToken = null;
      }
    })
    .addCase(loginEmployee.rejected, (state, action) => {
      state.employeeLoading = false;
      state.error = action.payload || action.error.message;
      state.passwordUpdateRequired = false;
      state.temporaryToken = null;
    })

    // Update Employee Password
    .addCase(updateEmployeePassword.pending, (state) => {
      state.employeeLoading = true;
      state.error = null;
    })
    .addCase(updateEmployeePassword.fulfilled, (state, action) => {
      state.employeeLoading = false;
      state.error = null;
    })
    .addCase(updateEmployeePassword.rejected, (state, action) => {
      state.employeeLoading = false;
      state.error = action.payload?.message || 'Failed to update password';
    });

  }
});
export const { logout, clearError, setUser, resetPasswordUpdateRequired } = employeeSlice.actions;
export default employeeSlice.reducer;